import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useDrawTravelAdvance, useOpenTravelChannel, useTravelBoard } from "../../lib/api";
import { Money, money, SectionCard, Stat } from "../labour/shared";

const DECISION_CLS: Record<string, string> = {
  Approved: "bg-green-50 text-status-green",
  Rejected: "bg-red-50 text-status-red",
  Pending: "bg-amber-50 text-status-amber",
};

/** Travel (WP-24 wave V, HRX-12): asking before you go, and reconciling after.
 *
 * `Travel Request`, `Travel Itinerary`, `Travel Request Costing` and `Purpose of Travel` all held zero rows,
 * and the last is why: `purpose_of_travel` is mandatory, so nobody could have raised a request even if they
 * had tried. Meanwhile 9,080,000 CDF of travel was claimed after the fact, because an expense claim was the
 * only travel document this site had.
 *
 * The screen deliberately shows no per-diem rate: the customer has not set one and WP-16 owns the statutory side. What
 * it gives an approver instead is what each route has actually cost, from claims already paid. */
export default function Travel({ company }: { company: string }) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const q = useTravelBoard(company || undefined);
  const openChannel = useOpenTravelChannel();
  const drawAdvance = useDrawTravelAdvance();

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const failing = d.state.filter((s) => !s.ok);
  const share = d.coverage.share;

  return (
    <div className="mt-4 space-y-4" data-travel>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-travel-strip>
        <Stat label={t("trv.requests")} value={d.summary.requests}
          hint={t("trv.requestsHint", { n: d.summary.awaiting })} />
        <Stat label={t("trv.upcoming")} value={d.summary.upcoming}
          hint={t("trv.upcomingHint")} />
        <Stat label={t("trv.authorised")} value={<Money value={d.summary.authorised} lng={lng} />}
          hint={t("trv.authorisedHint", { n: d.summary.approved })} />
        <Stat label={t("trv.unrequested")} value={<Money value={d.unrequested.amount} lng={lng} />}
          cls={d.unrequested.claims ? "text-status-amber" : "text-status-green"}
          hint={t("trv.unrequestedHint", { count: d.unrequested.claims })} />
        <Stat label={t("trv.covered")}
          value={share === null ? "—" : `${Math.round(share * 100)}%`}
          cls={share !== null && share < 0.5 ? "text-status-amber" : undefined}
          hint={t("trv.coveredHint")} />
      </div>

      {failing.length > 0 && (
        <SectionCard title={t("trv.findings")} aside={t("trv.findingsN", { n: failing.length, of: d.state.length })}>
          <ul>
            {failing.map((s) => (
              <li key={s.key} className="flex flex-wrap items-start justify-between gap-3 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
                data-travel-finding={s.key}>
                <div className="flex min-w-0 flex-1 items-start gap-2">
                  <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-status-amber" />
                  <div>
                    <div className="font-semibold">{t(`trv.check.${s.key}.title`)}</div>
                    <p className="mt-0.5 max-w-3xl text-sm text-brand-grey">
                      {t(`trv.check.${s.key}.bad`, {
                        ...s.facts,
                        // a finding that says "9080000 CDF" reads like a serial number; the strip above it
                        // says 9.08 M and the sentence should agree with it
                        ...(typeof s.facts.amount === "number"
                          ? { amount: money(s.facts.amount, lng).full }
                          : {}),
                      }) as string}
                    </p>
                  </div>
                </div>
                {d.permissions.manage && s.key === "travel_channel" && (
                  <button onClick={() => openChannel.mutate()} disabled={openChannel.isPending}
                    data-travel-open-channel
                    className="shrink-0 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    {t("trv.openChannel")}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <SectionCard title={t("trv.register")} aside={t("trv.noticeHint", { days: d.notice_days })}>
        {d.requests.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-travel-empty>{t("trv.noRequests")}</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {d.requests.map((r) => (
                <tr key={r.name} className="border-t border-brand-grey-light align-top first:border-t-0"
                  data-travel-request={r.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.employee_name}</div>
                    <div className="max-w-2xl text-[12px] text-brand-grey">{r.description}</div>
                    <div className="text-[11px] text-brand-grey">
                      {r.purpose_of_travel} · {r.itinerary.map((l) => `${l.travel_from ?? "?"}→${l.travel_to ?? "?"}`).join(", ")}
                      {r.itinerary[0]?.mode_of_travel ? ` · ${r.itinerary[0].mode_of_travel}` : ""}
                    </div>
                    <div className="text-[11px] text-brand-grey">
                      {r.os_departure ? t("trv.departs", { date: r.os_departure, count: r.window.days ?? 0 }) : t("trv.noDates")}
                      {r.notice.retrospective && (
                        <span className="ml-2 rounded bg-brand-grey-light px-1.5 py-0.5 text-[10px] font-semibold text-brand-grey"
                          data-travel-retrospective={r.name}>{t("trv.retrospective")}</span>
                      )}
                      {r.notice.short_notice && (
                        <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-status-amber">
                          {t("trv.shortNotice", { n: r.notice.days ?? 0 })}
                        </span>
                      )}
                    </div>
                    {r.faults.length > 0 && (
                      <div className="text-[11px] text-status-red" data-travel-faults={r.name}>
                        {r.faults.map((f) => t(`trv.fault.${f}`)).join(" · ")}
                      </div>
                    )}
                    {r.os_decision === "Rejected" && r.os_decision_note && (
                      <p className="mt-1 max-w-2xl border-l-2 border-brand-grey-light pl-2 text-[11px] italic text-brand-grey">
                        {r.os_decision_note}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${DECISION_CLS[r.os_decision ?? "Pending"] ?? ""}`}>
                      {t(`trv.decision.${r.os_decision ?? "Pending"}`)}
                    </span>
                    <div className="mt-1 text-[12px] font-semibold"><Money value={r.costing.funded} lng={lng} /></div>
                    {r.costing.sponsored > 0 && (
                      <div className="text-[11px] text-brand-grey">{t("trv.sponsored")} <Money value={r.costing.sponsored} lng={lng} /></div>
                    )}
                    {r.window.advance_requested > 0 && (
                      <div className="text-[11px] text-brand-grey" data-travel-advance={r.name}>
                        {r.advances.length
                          ? t("trv.advanceRaised", { n: r.advances.length })
                          : t("trv.advanceAsked")}{" "}
                        <Money value={r.window.advance_requested} lng={lng} />
                      </div>
                    )}
                    {d.permissions.manage && r.os_decision === "Approved"
                      && r.window.advance_requested > 0 && r.advances.length === 0 && (
                      <button onClick={() => drawAdvance.mutate({ request: r.name })}
                        disabled={drawAdvance.isPending} data-travel-draw={r.name}
                        className="mt-1 rounded border border-brand-primary px-2 py-0.5 text-[11px] font-semibold text-brand-primary disabled:opacity-50">
                        {t("trv.drawAdvance")}
                      </button>
                    )}
                    {r.settlement.settled && (
                      <div className={`text-[11px] ${r.settlement.over ? "text-status-red" : "text-brand-grey"}`}>
                        {t("trv.claimed")} <Money value={r.settlement.claimed} lng={lng} />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>

      <SectionCard title={t("trv.priceBook")}
        aside={t("trv.pricedOf", { priced: d.price_book.priced, unparsed: d.price_book.unparsed })}>
        {d.price_book.routes.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey">{t("trv.noRoutes")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wide text-brand-grey">
              <tr className="border-b border-brand-grey-light">
                <th className="px-4 py-2 text-left font-semibold">{t("trv.route")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("trv.trips")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("trv.low")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("trv.high")}</th>
                <th className="px-4 py-2 text-right font-semibold">{t("trv.spread")}</th>
              </tr>
            </thead>
            <tbody>
              {d.price_book.routes.map((r) => (
                <tr key={r.route} className="border-t border-brand-grey-light first:border-t-0"
                  data-travel-route={r.route}>
                  <td className="px-4 py-2 font-medium">{r.route}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.trips}</td>
                  <td className="px-4 py-2 text-right tabular-nums"><Money value={r.low} lng={lng} /></td>
                  <td className="px-4 py-2 text-right tabular-nums"><Money value={r.high} lng={lng} /></td>
                  <td className={`px-4 py-2 text-right tabular-nums ${r.wide ? "text-status-amber font-semibold" : "text-brand-grey"}`}>
                    {r.trips > 1 ? `${Math.round(r.spread * 100)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey">
          {t("trv.priceBookNote", { pct: Math.round(d.price_book.tolerance * 100) })}
        </p>
      </SectionCard>

      {openChannel.isError && <ErrorBox error={openChannel.error} />}
      {drawAdvance.isError && <ErrorBox error={drawAdvance.error} />}
    </div>
  );
}
